import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $initialAccountsStore, $initialCardsStore } from '@lib/store';
import { PaletteUtils } from '@shared/utils';
import { AccountType, CardKind } from '@shared/api';

export const useTabs = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const accountsStore = useUnit($initialAccountsStore.store);
  const cardsStore = useUnit($initialCardsStore.store);

  const [tab, setTab] = useState<AccountType | null>(null);

  const { t } = useTranslation();

  const loading = useMemo(() => accountsStore.loading || cardsStore.loading, [accountsStore.loading, cardsStore.loading]);
  const hasPayment = useMemo(() => accountsStore.data.some((account) => account.type === AccountType.Payment), [accountsStore.data]);
  const hasCard = useMemo(() => cardsStore.data.length > 0 && cardsStore.data.some((card) => card.cardKind === CardKind.Credit), [cardsStore.data]);

  useEffect(() => {
    if (loading) return;
    const tab = location.hash.replace('#', '');
    if (hasPayment && hasCard && (tab === AccountType.Payment || tab === AccountType.Card)) setTab(tab);
    else if (hasPayment && hasCard) setTab(AccountType.Payment);
    else if (hasPayment) setTab(AccountType.Payment);
    else if (hasCard) setTab(AccountType.Card);
    else setTab(null);
  }, [loading, hasPayment, hasCard]);

  const changeTabHandler = useCallback((tab: AccountType) => {
    setTab(tab);
    navigate(`#${tab}`, { replace: true });
  }, []);

  const tabs = useMemo(
    () => [
      { label: t(`accountType.payment${PaletteUtils.isInsigniaPalette ? '' : 's'}`), value: AccountType.Payment },
      { label: t(`accountType.card${PaletteUtils.isInsigniaPalette ? '' : 's'}`), value: AccountType.Card },
    ],
    [t],
  );
  const value = useMemo(
    () => ({
      loading,
      hasCard,
      hasPayment,
      tabs,
      tab,
      onChangeTab: changeTabHandler,
    }),
    [loading, hasCard, hasPayment, tabs, tab, changeTabHandler],
  );

  return value;
};
